import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MatchReportModal from '../components/MatchReportModal';
import {
  getFixtures,
  createFixture,
  updateFixture,
  deleteFixture,
  getTeams,
  getPlayers,
  getFixturesSyncConfig,
  updateFixturesSyncConfig,
  syncFixturesNow,
  Fixture,
  FixtureInput,
  FixtureStatus,
  Player,
  Team,
} from '../api/client';
import './Fixtures.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_LABELS: Record<FixtureStatus, string> = {
  scheduled: 'Scheduled',
  played: 'Played',
  postponed: 'Postponed',
  cancelled: 'Cancelled',
};

const emptyForm: FixtureInput = {
  teamId: '',
  date: '',
  koTime: '',
  homeAway: 'home',
  opponent: '',
  competition: 'League',
  status: 'scheduled',
  notes: '',
  potmPlayerId: '',
};

function fmtDate(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
}

function monthLabel(date: string): string {
  const [y, m] = date.split('-').map(Number);
  return `${MONTHS[(m || 1) - 1]} ${y}`;
}

function resultOf(f: Fixture): 'W' | 'D' | 'L' | null {
  if (f.status !== 'played' || f.goalsFor === undefined || f.goalsAgainst === undefined) return null;
  return f.goalsFor > f.goalsAgainst ? 'W' : f.goalsFor < f.goalsAgainst ? 'L' : 'D';
}

/**
 * Auto-sync panel: map each team to its FA Full-Time team page and the scheduled
 * runs (Wednesday fixtures, weekend and Monday results) keep it current. Manual fixtures are never
 * overwritten — only synced rows are managed.
 */
function SyncPanel({ teams, onSynced }: { teams: Team[]; onSynced: () => void }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [feeds, setFeeds] = useState<Array<{ teamId: string; input: string }>>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getFixturesSyncConfig()
      .then((c) => {
        setEnabled(c.enabled);
        setLastSyncedAt(c.lastSuccessAt);
        setFeeds(c.feeds.map((f) => ({ teamId: f.teamId, input: f.fullTimeTeamId })));
        if (c.feeds.length || c.enabled) setOpen(true);
      })
      .catch(() => { /* first-time: no config yet */ });
  }, []);

  const cleanFeeds = () => feeds.filter((f) => f.teamId && f.input.trim())
    .map((f) => ({ teamId: f.teamId, fullTimeTeamId: f.input.trim() }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const c = await updateFixturesSyncConfig({ enabled, feeds: cleanFeeds() });
      setEnabled(c.enabled);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

/**
 * What a club sees when a sync does not come back with matches.
 *
 * A fetch that fails on our side is ours to deal with, not something to hand a
 * club as an error code. Dan at Cottenham wired six feeds correctly and was told
 * "Sync error: Full-Time fetch failed: HTTP 403", which reads as though he had
 * done something wrong. He had not.
 *
 * A feed that does not exist IS the club's to fix, so that one still says so
 * plainly. Guessing wrong in that direction would leave someone waiting forever
 * for a sync that can never work.
 */
function syncFailureMessage(error?: string): string {
  const e = (error || '').toLowerCase();
  if (e.includes('404') || e.includes('not found')) {
    return 'One of those Full-Time links could not be found. Worth checking the team ID.';
  }
  return 'Saved. These will come through on the next sync.';
}

  const runNow = async () => {
    setSyncing(true); setMsg('');
    try {
      const r = await syncFixturesNow(cleanFeeds());
      setMsg(r.ok
        ? `Synced ${r.fetched} matches — ${r.written} updated, ${r.removed} removed.`
        : syncFailureMessage(r.error));
      if (r.ok) setLastSyncedAt(new Date().toISOString());
      onSynced();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (!open) {
    return (
      <button className="fx-sync-toggle" onClick={() => setOpen(true)}>
        ⚡ Set up auto-sync from FA Full-Time
      </button>
    );
  }

  return (
    <div className="fx-sync">
      <div className="fx-sync-head">
        <h2>Auto-sync from FA Full-Time</h2>
        <label className="fx-sync-enable">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Sync automatically</span>
        </label>
      </div>
      <p className="fx-sync-note">
        Link each team to its FA Full-Time page: paste the page link (or its id). New and
        changed fixtures come in every Wednesday morning, and results on Saturday and Sunday
        evenings and Monday morning. Your manually added fixtures are never touched. You only
        link once: the same page rolls over to each new season.
      </p>

      {feeds.map((f, i) => (
        <div key={i} className="fx-sync-feed">
          <select value={f.teamId} onChange={(e) => setFeeds(feeds.map((x, j) => j === i ? { ...x, teamId: e.target.value } : x))}>
            <option value="">— team —</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input
            type="text" placeholder="fulltime.thefa.com/displayTeam.html?id=…"
            value={f.input}
            onChange={(e) => setFeeds(feeds.map((x, j) => j === i ? { ...x, input: e.target.value } : x))}
          />
          <button className="fx-sync-rm" title="Remove" onClick={() => setFeeds(feeds.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button className="fx-sync-add" onClick={() => setFeeds([...feeds, { teamId: '', input: '' }])}>
        + Add a team feed
      </button>

      <div className="fx-sync-actions">
        <button className="btn btn-secondary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-primary" onClick={runNow} disabled={syncing || cleanFeeds().length === 0}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        {lastSyncedAt && <span className="fx-sync-last">Last synced {new Date(lastSyncedAt).toLocaleString('en-GB')}</span>}
      </div>
      {msg && <div className="fx-sync-msg">{msg}</div>}
    </div>
  );
}

function Fixtures() {
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Fixture | null>(null);
  // Pilot flag, operator-set. Absent for every club but gideon, so the button
  // simply is not there rather than being there and 404ing.
  const { plan, features } = useAuth();
  // Club + Pro get match reports by paying; the old pilot flag still grants it
  // to anyone an operator has switched on by hand. Free sees the button and is
  // told what it is, rather than never learning the feature exists.
  const matchReportsOn = plan === 'club' || plan === 'pro' || features?.matchReports === true;
  const [reportLocked, setReportLocked] = useState(false);
  const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});
  const [reportFor, setReportFor] = useState<Fixture | null>(null);
  const [reportMsg, setReportMsg] = useState('');
  const [form, setForm] = useState<FixtureInput>(emptyForm);
  const [goals, setGoals] = useState({ for: '', against: '' });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [fx, ts, ps] = await Promise.all([getFixtures(), getTeams().catch(() => []), getPlayers().catch(() => [])]);
      setFixtures(fx);
      setTeams(ts);
      setPlayers(ps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setGoals({ for: '', against: '' });
    setModalOpen(true);
  };

  const openEdit = (f: Fixture, asResult = false) => {
    setEditing(f);
    setForm({
      teamId: f.teamId || '',
      date: f.date,
      koTime: f.koTime || '',
      homeAway: f.homeAway,
      opponent: f.opponent,
      competition: f.competition,
      status: asResult ? 'played' : f.status,
      notes: f.notes || '',
      potmPlayerId: f.potm?.playerId || '',
    });
    setGoals({
      for: f.goalsFor !== undefined ? String(f.goalsFor) : '',
      against: f.goalsAgainst !== undefined ? String(f.goalsAgainst) : '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload: FixtureInput = { ...form };
    if (form.status === 'played') {
      payload.goalsFor = Number(goals.for);
      payload.goalsAgainst = Number(goals.against);
      payload.potmPlayerId = form.potmPlayerId || null;
    } else {
      // Off played, the award is hidden on the website but kept.
      delete payload.potmPlayerId;
    }
    try {
      if (editing) {
        await updateFixture(editing.fixtureId, payload);
      } else {
        await createFixture(payload);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fixture');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f: Fixture) => {
    if (!window.confirm(`Delete ${f.homeAway === 'home' ? 'vs' : 'at'} ${f.opponent} on ${f.date}?`)) return;
    try {
      await deleteFixture(f.fixtureId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete fixture');
    }
  };

  const teamName = (id?: string) => teams.find(t => t.id === id)?.name || '';
  const playerName = (id?: string) => {
    const p = players.find(x => x.playerId === id);
    return p ? `${p.firstName} ${p.lastName || ''}`.trim() : '';
  };

  const squad = players
    .filter(p => (p.teamId === form.teamId && p.status !== 'archived') || p.playerId === form.potmPlayerId)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));
  const potmPlayer = players.find(p => p.playerId === form.potmPlayerId);
  const potmHint = !potmPlayer ? ''
    : teams.find(t => t.id === form.teamId)?.hideSquad
      ? "This won't show on your website: this team's squad is hidden."
      : potmPlayer.status === 'archived' || !potmPlayer.publicFields?.showOnSquadPage
        ? `This won't show on your website because ${potmPlayer.firstName}'s profile isn't public.`
        : '';

  // Newest first for the admin: the next fixture / latest result is the working end
  const sorted = [...fixtures].sort((a, b) =>
    b.date.localeCompare(a.date) || (b.koTime || '').localeCompare(a.koTime || ''));

  const groups: Array<{ label: string; key: string; rows: Fixture[] }> = [];
  let lastGroup: { label: string; key: string; rows: Fixture[] } | undefined;
  for (const f of sorted) {
    const label = monthLabel(f.date);
    if (!lastGroup || lastGroup.label !== label) {
      lastGroup = { label, key: f.date.slice(0, 7), rows: [] };
      groups.push(lastGroup);
    }
    lastGroup.rows.push(f);
  }

  // Newest first puts months that have not happened yet at the TOP, so a club
  // scrolled past them every time to reach the result they came to enter. Future
  // months start folded; this month and everything behind it stay open. A click
  // overrides either way and sticks for the visit.
  const thisMonth = new Date().toISOString().slice(0, 7);
  // A club whose season has not started yet has nothing but future months, and
  // folding all of them would open on a page that looks empty (outwell-swifts-fc
  // today). Groups are newest first, so the last one is the soonest: keep it open.
  const soonest = groups.length && groups.every(g => g.key > thisMonth)
    ? groups[groups.length - 1]!.key : '';
  const isOpen = (g: { key: string }) =>
    openMonths[g.key] ?? (g.key <= thisMonth || g.key === soonest);
  const toggleMonth = (key: string, open: boolean) =>
    setOpenMonths(prev => ({ ...prev, [key]: !open }));

  return (
    <div className="fx-page">
      <div className="fx-header">
        <div>
          <h1>Fixtures &amp; Results</h1>
          <p className="fx-sub">
            Matches shown on your public site. Enter results after the final whistle —
            season grouping and form work themselves out.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Add Fixture</button>
      </div>

      <SyncPanel teams={teams} onSynced={load} />

      {error && <div className="fx-error">{error}</div>}

      {reportMsg && <div className="fx-report-msg">{reportMsg}</div>}
      {reportFor && (
        <MatchReportModal
          fixtureId={reportFor.fixtureId}
          onClose={() => setReportFor(null)}
          onSaved={(m) => { setReportMsg(m); void load(); }}
        />
      )}

      {loading ? (
        <p className="fx-status">Loading fixtures...</p>
      ) : fixtures.length === 0 ? (
        <div className="fx-empty">
          <p>No fixtures yet. Add your first match and it appears on the website instantly.</p>
          <button className="btn btn-primary" onClick={openCreate}>+ Add Fixture</button>
        </div>
      ) : (
        groups.map(group => (
          <div key={group.label} className="fx-group">
            <button
              type="button"
              className="fx-group-heading"
              aria-expanded={isOpen(group)}
              onClick={() => toggleMonth(group.key, isOpen(group))}
            >
              <span className={`fx-group-caret ${isOpen(group) ? 'is-open' : ''}`} aria-hidden="true">›</span>
              {group.label}
              <span className="fx-group-count">{group.rows.length}</span>
            </button>
            {isOpen(group) && (
            <div className="fx-list">
              {group.rows.map(f => {
                const r = resultOf(f);
                return (
                  <div key={f.fixtureId} className={`fx-card ${f.status === 'cancelled' ? 'fx-card-off' : ''}`}>
                    <div className="fx-when">
                      <span className="fx-date">{fmtDate(f.date)}</span>
                      <span className="fx-ko">{f.koTime || '—'}</span>
                    </div>
                    <div className="fx-match">
                      <span className="fx-opponent">
                        {f.homeAway === 'home' ? 'vs' : 'at'} {f.opponent}
                      </span>
                      <span className="fx-meta">
                        {f.competition}
                        {f.teamId && teamName(f.teamId) ? ` · ${teamName(f.teamId)}` : ''}
                        {f.notes ? ` · ${f.notes}` : ''}
                        {f.potm && playerName(f.potm.playerId) ? ` · ★ ${playerName(f.potm.playerId)}` : ''}
                      </span>
                    </div>
                    <div className="fx-outcome">
                      {f.status === 'played' ? (
                        <>
                          <span className="fx-score">
                            {f.goalsFor === undefined || f.goalsAgainst === undefined
                              ? 'W/O' : `${f.goalsFor}–${f.goalsAgainst}`}
                          </span>
                          {r && <span className={`fx-chip fx-chip-${r.toLowerCase()}`}>{r}</span>}
                        </>
                      ) : f.status === 'scheduled' ? (
                        /* Straight into the combined editor when the club can
                           report: the score and who played are one event, and
                           splitting them meant the second half got skipped.
                           Falls back to the plain fixture form otherwise. */
                        <button className="btn btn-secondary fx-result-btn"
                          onClick={() => (matchReportsOn && f.teamId ? setReportFor(f) : openEdit(f, true))}>
                          Enter result
                        </button>
                      ) : (
                        <span className="fx-status-tag">{STATUS_LABELS[f.status]}</span>
                      )}
                    </div>
                    <div className="fx-actions">
                      {f.status === 'played' && f.teamId && (
                        matchReportsOn ? (
                          <button className="fx-icon-btn" title="Match report"
                                  onClick={() => setReportFor(f)}>📋</button>
                        ) : (
                          <button className="fx-icon-btn fx-icon-locked"
                                  title="Match report — on Club and Pro"
                                  onClick={() => setReportLocked(true)}>📋</button>
                        )
                      )}
                      <button className="fx-icon-btn" title="Edit" onClick={() => openEdit(f)}>✏️</button>
                      <button className="fx-icon-btn" title="Delete" onClick={() => handleDelete(f)}>🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        ))
      )}

      {/* Free clubs see the report button and find out what it does, instead of
          the feature being invisible. The server 404s them either way. */}
      {reportLocked && (
        <div className="modal-overlay" onClick={() => setReportLocked(false)}>
          <div className="modal fx-locked-modal" onClick={e => e.stopPropagation()}>
            <h2>Match reports</h2>
            <p>
              Record who played, who scored and who got player of the match, for every
              fixture. Appearances and goals then build up on each player's profile
              across the season.
            </p>
            <p className="fx-locked-note">Included on Club and Pro.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setReportLocked(false)}>Not now</button>
              <Link className="btn btn-primary" to="/upgrade">See the plans</Link>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fx-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="fx-modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Fixture' : 'Add Fixture'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Date *</span>
                  <input type="date" required value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })} />
                </label>
                <label className="fx-field">
                  <span>Kick-off</span>
                  <input type="time" value={form.koTime}
                    onChange={e => setForm({ ...form, koTime: e.target.value })} />
                </label>
              </div>

              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Home / Away</span>
                  <select value={form.homeAway}
                    onChange={e => setForm({ ...form, homeAway: e.target.value as 'home' | 'away' })}>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </label>
                <label className="fx-field">
                  <span>Competition</span>
                  <input type="text" maxLength={40} value={form.competition}
                    placeholder="League / Cup / Friendly"
                    onChange={e => setForm({ ...form, competition: e.target.value })} />
                </label>
              </div>

              <label className="fx-field">
                <span>Opponent *</span>
                <input type="text" required maxLength={80} value={form.opponent}
                  onChange={e => setForm({ ...form, opponent: e.target.value })} />
              </label>

              {teams.length > 0 && (
                <label className="fx-field">
                  <span>Team</span>
                  <select value={form.teamId}
                    onChange={e => setForm({ ...form, teamId: e.target.value, potmPlayerId: '' })}>
                    <option value="">— Whole club / first team —</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
              )}

              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Status</span>
                  <select value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value as FixtureStatus })}>
                    {(Object.keys(STATUS_LABELS) as FixtureStatus[]).map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </label>
                {form.status === 'played' && (
                  <div className="fx-scores">
                    <label className="fx-field fx-field-goal">
                      <span>Us</span>
                      <input type="number" min={0} max={99} required value={goals.for}
                        onChange={e => setGoals({ ...goals, for: e.target.value })} />
                    </label>
                    <span className="fx-score-dash">–</span>
                    <label className="fx-field fx-field-goal">
                      <span>Them</span>
                      <input type="number" min={0} max={99} required value={goals.against}
                        onChange={e => setGoals({ ...goals, against: e.target.value })} />
                    </label>
                  </div>
                )}
              </div>

              {form.status === 'played' && (
                <label className="fx-field">
                  <span>Player of the Match</span>
                  <select value={form.potmPlayerId || ''} disabled={!form.teamId || squad.length === 0}
                    onChange={e => setForm({ ...form, potmPlayerId: e.target.value })}>
                    <option value="">
                      {!form.teamId ? 'Choose a team first' : squad.length === 0 ? 'No players on this team yet' : 'None'}
                    </option>
                    {squad.map(p => (
                      <option key={p.playerId} value={p.playerId}>{`${p.firstName} ${p.lastName || ''}`.trim()}</option>
                    ))}
                  </select>
                  {potmHint && <small className="fx-potm-hint">{potmHint}</small>}
                </label>
              )}

              <label className="fx-field">
                <span>Notes</span>
                <input type="text" maxLength={300} value={form.notes}
                  placeholder="e.g. walkover, ground change"
                  onChange={e => setForm({ ...form, notes: e.target.value })} />
                <small className="fx-notes-hint">
                  Left empty, the goals from a match report or KiCKS fill in here. Anything you write stays as you wrote it.
                </small>
              </label>

              <div className="fx-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Fixture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Fixtures;
